import { debounce } from 'lodash';
import { FunctionComponent, useRef, useEffect, useState, useCallback } from 'react';

interface NicheProps extends RewindSlideProps {
  value: number;
}

const Niche: FunctionComponent<NicheProps> = (props) => {
  const { value, active } = props;
  const [progressValue, setProgressValue] = useState(0);
  const cursorRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const positionCursor = useCallback((_progressValue: number) => {
    const progressBar = document.getElementById(`${window.innerWidth < 1024 ? 'vertical-' : ''}mainstreammeter`);

    if (progressBar && cursorRef.current && containerRef.current) {
      cursorRef.current.style.opacity = '1';

      if (window.innerWidth >= 1024) {
        const cursorPosition = Math.ceil((progressBar.clientWidth * _progressValue) / 100) - 82;
        cursorRef.current.style.top = '-93px';
        cursorRef.current.style.left = `${cursorPosition}px`;
      } else {
        const cursorPosition = Math.ceil((progressBar.clientHeight * _progressValue) / 100) + progressBar.offsetTop - 20;
        cursorRef.current.style.left = `${(containerRef.current.clientWidth / 2) - 25}px`;
        cursorRef.current.style.top = `${cursorPosition}px`;
      }
    }
  }, []);

  useEffect(() => {
    positionCursor(0);

    if (active) {
      const timeoutHandler = setTimeout(() => {
        if (cursorRef.current) {
          cursorRef.current.style.top = '0';
        }

        const _v = Math.floor(value * 100);
        setProgressValue(_v);
        positionCursor(_v);
      }, 1000);

      return () => {
        clearTimeout(timeoutHandler);
      };
    }

    setProgressValue(0);
    positionCursor(0);

    return () => {};
  }, [active, positionCursor, value]);

  useEffect(() => {
    const progressBar = document.getElementById('mainstreammeter');
    if (progressBar && progressValue >= 0) {
      const resizeHandler = debounce(() => {
        positionCursor(progressValue);
      }, 700);

      window.addEventListener('resize', () => {
        if (cursorRef.current) {
          cursorRef.current.style.opacity = '0';

          if (window.innerWidth < 1024) {
            cursorRef.current.style.top = '100px';
          }
        }
        resizeHandler();
      });

      positionCursor(progressValue);

      return () => {
        window.removeEventListener('resize', resizeHandler);
      };
    }

    return () => {};
  }, [cursorRef, positionCursor, progressValue]);

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-xl xs:text-xl sm:text-2xl lg:text-3xl xl:text-4xl xs:px-4 w-full max-w-[450px] lg:max-w-[800px] m-auto slide-up">
        What do your
        {' '}
        <span className="text-dark-tangerine">selections</span>
        {' '}
        say about you?
      </h2>
      <div
        ref={containerRef}
        className="relative mt-8 sm:mt-4 md:mt-10 lg:mt-40 font-ubuntu font-bold h-[65vh] lg:h-auto slide-down delay-[600ms]
          flex flex-col items-center justify-center"
      >
        <div className="lg:hidden text-lg relative mx-auto uppercase table break-words w-36 opacity-80">Hidden Gems Hunter</div>
        <div
          role="progressbar"
          id="mainstreammeter"
          className="hidden lg:block w-[45vh] lg:w-[800px] xl:w-[900px] 2xl:w-[1100px] h-[49px] bg-blanched-almond rounded-lg"
          aria-valuenow={progressValue}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="bg-dark-tangerine h-[49px] rounded-l-lg transition-[width] duration-700 ease-in-out"
            style={{ width: `${progressValue}%` }}
          />
        </div>
        <div
          role="progressbar"
          id="vertical-mainstreammeter"
          className="lg:hidden h-[100%] w-[49px] bg-blanched-almond rounded-lg mt-8 mb-8"
          aria-valuenow={progressValue}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="bg-dark-tangerine w-[49px] rounded-t-lg transition-[height] duration-700 ease-in-out"
            style={{ height: `${progressValue}%` }}
          />
        </div>
        <div ref={cursorRef} className="flex flex-row lg:flex-col items-center absolute top-[-93px] transition-[top,left] duration-700 ease-in-out sm:mt-[-10px] lg:mt-0">
          <div className="lg:hidden bg-affair w-[70px] h-[8px]"/>
          <div className="text-base sm:text-[1.375rem] bg-affair p-1 sm:p-3 w-[120px] sm:w-[170px] rounded-2xl translate-x-[-3px]">You are here</div>
          <div className="hidden lg:block bg-affair w-[8px] h-[90px]"/>
        </div>
        <div className="hidden uppercase text-[1.25rem] mt-6 lg:flex lg:flex-row lg:w-[800px] xl:w-[900px] 2xl:w-[1100px]">
          <div className="mr-auto table break-words w-40 text-left">Hidden Gems Hunter</div>
          <div className="ml-auto table break-words w-40 text-right">Favourites Fanatic</div>
        </div>
        <div className="lg:hidden text-lg relative mx-auto uppercase table break-words w-36 opacity-80">Favourites Fanatic</div>
      </div>
    </div>
  );
};

export default Niche;
